import { useQuery } from "@tanstack/react-query";

import { MAIL_SEND_STATUS_LABELS } from "@devengo/shared";

import { statusTone, type MailSend } from "../components/send-document";
import { Badge, Card, ErrorBox, tableClass, tdClass, thClass } from "../components/ui";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";

/** Everything mailed to the company, with who sent it, to whom and how it went (FR-008). */
export function MailHistory({ companyId }: { companyId: string }) {
  const sends = useQuery({
    queryKey: ["mail-sends", companyId],
    queryFn: () => api.get<MailSend[]>(`/api/companies/${companyId}/mail-sends`),
    refetchInterval: (query) =>
      query.state.data?.some((s) => s.status === "queued") ? 5000 : false,
  });
  return (
    <Card title="Documentos enviados por mail">
      <ErrorBox error={sends.error} />
      {sends.data?.length === 0 ? (
        <p className="text-sm text-brand-muted">
          Todavía no se mandó nada. Desde la Caja IVA o los Libros IVA, «Enviar por mail».
        </p>
      ) : (
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={thClass}>Fecha</th>
              <th className={thClass}>Documento</th>
              <th className={thClass}>Para</th>
              <th className={thClass}>Envió</th>
              <th className={thClass}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {sends.data?.map((s) => (
              <tr key={s.id}>
                <td className={`${tdClass} tabular whitespace-nowrap`}>
                  {formatDateTime(s.queuedAt ?? s.createdAt)}
                </td>
                <td className={tdClass}>
                  {s.documentLabel}
                  {s.attachmentName ? (
                    <span className="block text-xs text-brand-muted">{s.attachmentName}</span>
                  ) : null}
                </td>
                <td className={tdClass}>{s.recipients.join(", ")}</td>
                <td className={tdClass}>{s.sentByName ?? ""}</td>
                <td className={tdClass}>
                  <Badge tone={statusTone(s.status)}>{MAIL_SEND_STATUS_LABELS[s.status]}</Badge>
                  {s.failureReason ? (
                    <span className="block text-xs text-red-700">{s.failureReason}</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
